import { addDoc, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { CashRegister, cashRegistersRef, CashRegisterStatus } from '@api/CashRegister/CashRegister';
import UserServ from '@api/Users/UserServ';
import store from '@store/index';
import { getCashRegisterMillis, getEndOfCashRegisterDay, isActiveCashRegister } from '@utils/CashRegisters';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import { cashText } from '@api/ActivityLogs/CashActivity';

export default {
    /**
     * Cajas que siguen ABIERTAS de días anteriores. `branchOfficeId: 'all'` para
     * todas las sucursales (panel del admin).
     *
     * Decisión del negocio (2026-08-17): esto **NO bloquea** abrir una caja nueva.
     * Si alguien no cerró su caja ayer, quien llega hoy no puede cerrarla por él
     * (no es su caja ni su conteo), así que bloquearlo lo dejaría sin trabajar por
     * un error ajeno. La caja vieja se queda abierta, y el control es que el ADMIN
     * la vea: el panel de cajas la muestra en un aviso arriba.
     *
     * La query filtra por status Y por sucursal (dos igualdades: Firestore las
     * resuelve con sus índices de un campo, sin índice compuesto); la fecha se
     * filtra en cliente. Antes traía las cajas abiertas de TODAS las sucursales
     * (72 el 2026-09-06) en cada montaje de la barra superior.
     */
    async getOpenCashRegisters(branchOfficeId?: string): Promise<CashRegister[]> {
        const effectiveBranchId = branchOfficeId ?? store.getState().auth.branchOfficeSelected?.id;
        const constraints = [where('status', '==', CashRegisterStatus.Open)];
        if (effectiveBranchId && effectiveBranchId !== 'all') {
            constraints.push(where('branchOfficeId', '==', effectiveBranchId));
        }
        const snapshot = await getDocs(query(cashRegistersRef, ...constraints));
        // `getCashRegisterMillis` y no un `new Date(register.date)`: una caja con
        // Timestamp CRUDO daba `NaN` y el orden quedaba a merced del comparador.
        const openedMillis = (register: CashRegister) => getCashRegisterMillis(register.date);
        return snapshot.docs
            .map((document) => ({ ...(document.data() as CashRegister), id: document.id }))
            .filter((register) => effectiveBranchId === 'all' || register.branchOfficeId === effectiveBranchId)
            .sort((a, b) => openedMillis(b) - openedMillis(a));
    },
    async getOpenCashRegistersFromPreviousDays(branchOfficeId?: string): Promise<CashRegister[]> {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const open = await this.getOpenCashRegisters(branchOfficeId);
        // ⚠️ Con `new Date(register.date)` un Timestamp crudo daba `NaN`, y
        // `NaN < hoy` es **false**: la caja vieja dejaba de bloquear sin un solo
        // error. `getCashRegisterMillis` devuelve 0, que sí es "vieja" — y el
        // 0 explícito se excluye porque no se puede afirmar nada de una caja sin
        // fecha (mismo criterio que `getBlockingCashRegisters`).
        return open.filter((register) => {
            const openedMillis = getCashRegisterMillis(register.date);
            return openedMillis > 0 && openedMillis < startOfToday.getTime();
        });
    },
    /**
     * La caja abierta HOY de una sucursal, si existe.
     *
     * Existe porque la caja vivía SOLO en el Redux del dispositivo que la abrió:
     * si la tablet de la sucursal abría caja, cualquier otra sesión (el admin, otra
     * tablet) no se enteraba y el POS pedía abrir otra. Con esto, al entrar se
     * adopta la caja que ya está abierta en la sucursal.
     *
     * A propósito solo mira las de HOY: si quedó una abierta de ayer NO se adopta
     * (mezclaría dos días de efectivo) — se abre una nueva y el admin ve la vieja
     * en el panel de cajas.
     */
    async getTodayOpenCashRegister(branchOfficeId?: string): Promise<CashRegister | undefined> {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const open = await this.getOpenCashRegisters(branchOfficeId);
        // Misma trampa al revés: `NaN >= hoy` también es false, así que una caja
        // legítima del día podía no adoptarse y el POS pedía abrir otra.
        return open.find((register) => getCashRegisterMillis(register.date) >= startOfToday.getTime());
    },
    async openCashRegister(amount: number): Promise<CashRegister> {
        const user = await UserServ.getUser();
        const date = new Date();
        const branchOfficeId = store.getState().auth.branchOfficeSelected?.id;
        const cashRegister: CashRegister = {
            status: CashRegisterStatus.Open,
            userId: user.uid,
            username: user.profile.name,
            branchOfficeId,
            date,
            initialCash: amount
        };
        const data = await addDoc(cashRegistersRef, cashRegister);
        cashRegister.id = data.id;
        await updateDoc(doc(cashRegistersRef, cashRegister.id), { ...cashRegister });
        await ActivityLogServ.addActivityLog(
            'cashRegister',
            cashRegister.id,
            `abrió la caja con el monto de: ${cashText(amount)}`
        );
        return cashRegister;
    },
    // branchOfficeId opcional: sin él usa la sucursal en operación (comportamiento
    // original); 'all' = sin filtro de sucursal (para admin en informe de ventas).
    async getCashRegistersByDate(date: Date, branchOfficeId?: string): Promise<CashRegister[]> {
        const cashRegisters: CashRegister[] = [];
        const effectiveBranchId = branchOfficeId ?? store.getState().auth.branchOfficeSelected?.id;
        const filters = [where('date', '>=', date)];
        if (effectiveBranchId !== 'all') {
            filters.push(where('branchOfficeId', '==', effectiveBranchId));
        }
        const queryDoc = query(cashRegistersRef, ...filters);
        const snapshot = await getDocs(queryDoc);
        if (snapshot.empty) {
            return cashRegisters;
        }
        snapshot.docs.map((doc: any) => {
            cashRegisters.push(doc.data() as CashRegister);
        });
        return cashRegisters;
    },
    // ⚠️ `getCashRegistersByDate` (arriba) NO acota por arriba: trae de esa fecha EN
    // ADELANTE, y así lo consume el informe de ventas. Para el panel de cajas del
    // admin, que mira UN día, hace falta un rango real [start, end).
    // También devuelve el id del doc: los registros viejos podrían no tenerlo dentro.
    async getCashRegistersByRange(startDate: Date, endDate: Date, branchOfficeId?: string): Promise<CashRegister[]> {
        const filters = [where('date', '>=', startDate), where('date', '<', endDate)];
        if (branchOfficeId && branchOfficeId !== 'all') {
            filters.push(where('branchOfficeId', '==', branchOfficeId));
        }
        const snapshot = await getDocs(query(cashRegistersRef, ...filters));
        return snapshot.docs.map((document) => ({ ...(document.data() as CashRegister), id: document.id }));
    },
    /**
     * Alta manual de una caja desde el panel del admin: una sucursal que no abrió
     * ese día, o una caja que se llevó en papel. NO pasa por `openCashRegister`
     * (ese es el flujo del POS y se queda exactamente igual): aquí la sucursal, la
     * fecha y el responsable los decide el admin, no la sesión en curso.
     */
    async createCashRegisterForDate(params: {
        branchOfficeId: string;
        date: Date;
        initialCash: number;
        status?: CashRegisterStatus;
    }): Promise<CashRegister> {
        const user = await UserServ.getUser();
        const cashRegister: CashRegister = {
            status: params.status || CashRegisterStatus.Open,
            userId: user.uid,
            username: user.profile.name,
            branchOfficeId: params.branchOfficeId,
            date: params.date,
            initialCash: params.initialCash
        };
        const data = await addDoc(cashRegistersRef, cashRegister);
        cashRegister.id = data.id;
        await updateDoc(doc(cashRegistersRef, cashRegister.id), { ...cashRegister });
        await ActivityLogServ.addActivityLog(
            'cashRegister',
            cashRegister.id,
            `dio de alta esta caja desde el panel de administración con el monto de: ${cashText(params.initialCash)}`
        );
        return cashRegister;
    },
    /**
     * Edición del admin: SOLO fondo inicial y fecha/hora de apertura — que es lo
     * que se captura mal. La sucursal y el responsable NO se tocan: moverlos
     * descuadraría cortes de caja e informes ya revisados.
     */
    async updateCashRegister(cashRegisterId: string, changes: { initialCash?: number; date?: Date }): Promise<void> {
        const payload: Record<string, any> = {};
        const detail: string[] = [];
        if (changes.initialCash !== undefined) {
            payload.initialCash = changes.initialCash;
            detail.push(`monto inicial a ${cashText(changes.initialCash)}`);
        }
        if (changes.date) {
            payload.date = changes.date;
            detail.push(`fecha de apertura a ${changes.date.toLocaleString()}`);
        }
        if (detail.length === 0) return;
        await updateDoc(doc(cashRegistersRef, cashRegisterId), payload);
        await ActivityLogServ.addActivityLog('cashRegister', cashRegisterId, `editó la caja: ${detail.join(' y ')}`);
    },
    async reopenCashRegister(cashRegisterId: string): Promise<void> {
        await updateDoc(doc(cashRegistersRef, cashRegisterId), { status: CashRegisterStatus.Open });
        await ActivityLogServ.addActivityLog('cashRegister', cashRegisterId, `reabrió la caja`);
    },
    /**
     * ¿Hay caja abierta de hoy en ESA sucursal, de verdad? La de la sesión (Redux) puede
     * estar vieja —otro aparato la cerró— y un gasto "de la caja" guardado con la caja
     * ya cerrada cae fuera de todo corte: le pasó a $20 k de Esteban de Antuñano el
     * 5-oct, capturados 14 minutos después del cierre. Por eso, cuando la sesión dice
     * que sí, se CONFIRMA contra Firestore. Lecturas: 0 si la sesión ya dice que no, 1
     * si dice que sí. **Si la lectura falla, manda la sesión**: nadie queda atrapado.
     */
    async confirmActiveCashRegister(branchOfficeId?: string): Promise<boolean> {
        const { auth, krispis } = store.getState();
        if (!branchOfficeId || branchOfficeId !== auth.branchOfficeSelected?.id) return false;
        const session = krispis.cashRegister;
        if (!isActiveCashRegister(session, branchOfficeId)) return false;
        if (!session?.id) return true;
        try {
            return isActiveCashRegister(await this.getCashRegister(session.id), branchOfficeId);
        } catch {
            return true;
        }
    },
    /** Una caja por id. `undefined` si el doc ya no existe */
    async getCashRegister(cashRegisterId: string): Promise<CashRegister | undefined> {
        const documentData = await getDoc(doc(cashRegistersRef, cashRegisterId));
        if (!documentData.exists()) return undefined;
        return { ...(documentData.data() as CashRegister), id: documentData.id };
    },
    async updateInitialAmount(cashRegisterId: string, newAmount: number): Promise<CashRegister> {
        await updateDoc(doc(cashRegistersRef, cashRegisterId), { initialCash: newAmount });
        await ActivityLogServ.addActivityLog(
            'cashRegister',
            cashRegisterId,
            `actualizó el monto inicial de la caja con: ${cashText(newAmount)}`
        );
        const documentData = await getDoc(doc(cashRegistersRef, cashRegisterId));
        return documentData.data() as CashRegister;
    },
    /**
     * Cierra la caja. Con `closing` guarda además el CORTE CIEGO: lo que se contó
     * físicamente, lo que el sistema esperaba y la diferencia. Sin `closing` se
     * comporta como siempre (lo usa el panel del admin para cerrar una caja vieja).
     *
     * `closedAt` se guarda SIEMPRE: es lo que permite repartir el dinero del día
     * entre las varias cajas de una sucursal (ver @utils/CashRegisters).
     */
    async closeCashRegister(
        cashRegisterId: string,
        closing?: { countedCash: number; expectedCash: number; note?: string }
    ): Promise<void> {
        const closedAt = new Date();
        const payload: Record<string, any> = { status: CashRegisterStatus.Closed, closedAt };
        let logDetail = 'cerró la caja';

        if (closing) {
            const user = await UserServ.getUser();
            const difference = closing.countedCash - closing.expectedCash;
            payload.countedCash = closing.countedCash;
            payload.expectedCash = closing.expectedCash;
            payload.difference = difference;
            payload.closedByUserId = user.uid;
            payload.closedByName = user.profile.name;
            if (closing.note) payload.closingNote = closing.note;
            logDetail =
                `cerró la caja con un conteo de ${cashText(closing.countedCash)} ` +
                `(esperado ${cashText(closing.expectedCash)}, diferencia ${cashText(difference)})`;
        }

        await updateDoc(doc(cashRegistersRef, cashRegisterId), payload);
        await ActivityLogServ.addActivityLog('cashRegister', cashRegisterId, logDetail);
        return;
    },

    /**
     * Cierra una caja que quedó abierta de un DÍA ANTERIOR, desde el candado que
     * aparece al abrir la del día (decisión de Irving, 2026-09-10).
     *
     * Es una operación distinta a `closeCashRegister`, no una variante, y por
     * dos razones que conviene no borrar:
     *
     * ① **`closedAt` es el fin de SU día, no ahora.** La ventana de una caja va
     *    de su apertura a su cierre; fecharla hoy la estiraría sobre los días
     *    intermedios y **se tragaría el dinero de las cajas de en medio**. Mismo
     *    criterio que `scripts/cerrar-cajas-abiertas.mjs`.
     * ② **NO calcula esperado ni diferencia.** Quien llega no contó el efectivo
     *    de un turno ajeno, así que un "corte ciego" aquí sería inventado:
     *    dejaría una diferencia falsa a nombre de alguien que ya se fue y
     *    ensuciaría para siempre la métrica de descuadres. En el panel queda
     *    "—" en Diferencia, que es la verdad. Si quien cierra sabe cuánto había,
     *    ese monto se guarda en `countedCash` y ya.
     *
     * Sí se registra QUIÉN lo cerró: es una acción con dinero de por medio.
     */
    async closeStaleCashRegister(
        cashRegister: CashRegister,
        options?: { countedCash?: number; note?: string }
    ): Promise<void> {
        const user = await UserServ.getUser();
        const closedAt = getEndOfCashRegisterDay(cashRegister);
        const payload: Record<string, any> = {
            status: CashRegisterStatus.Closed,
            closedAt,
            closedByUserId: user.uid,
            closedByName: user.profile.name,
            closingNote:
                options?.note ||
                `Cierre tardío: la caja quedó abierta de un día anterior y se cerró al abrir la del ${new Date().toLocaleDateString(
                    'es-MX'
                )}.`
        };
        if (typeof options?.countedCash === 'number') {
            payload.countedCash = options.countedCash;
        }
        await updateDoc(doc(cashRegistersRef, cashRegister.id as string), payload);
        await ActivityLogServ.addActivityLog(
            'cashRegister',
            cashRegister.id as string,
            typeof options?.countedCash === 'number'
                ? `cerró una caja rezagada de otro día declarando ${cashText(options.countedCash)} en efectivo (sin corte ciego)`
                : 'cerró una caja rezagada de otro día sin conteo'
        );
        return;
    }
};
